import React from 'react';

export interface LabelStatus {
    is_generating: boolean;
    last_status_message?: string;
    current_step?: string;
    processed_files: number;
    total_files: number;
    error?: string;
    updated_at?: string;
}

interface LabelMonitoringModalProps {
    status: LabelStatus | null;
    onClose: () => void;
    algorithmName?: string;
}

const STEPS = [
    { key: 'INIT', label: 'Inizializzazione' },
    { key: 'SYNC', label: 'Sincronizzazione file' },
    { key: 'RUNNING', label: 'Esecuzione Algoritmo' },
    { key: 'APPLYING', label: 'Salvataggio nel Database' },
];

const STEP_ORDER = STEPS.map(s => s.key);

function getStepState(stepKey: string, currentStep: string | undefined, isGenerating: boolean, hasError: boolean): 'pending' | 'active' | 'done' | 'error' {
    if (!currentStep) return 'pending';

    if (currentStep === 'COMPLETED') return 'done';
    if (currentStep === 'ERROR' || hasError) {
        if (currentStep === stepKey) return 'error';
        const currentIdx = STEP_ORDER.indexOf(currentStep);
        const thisIdx = STEP_ORDER.indexOf(stepKey);
        if (thisIdx < currentIdx) return 'done';
        if (thisIdx === currentIdx) return 'error';
        return 'pending';
    }

    const currentIdx = STEP_ORDER.indexOf(currentStep);
    const thisIdx = STEP_ORDER.indexOf(stepKey);

    if (currentIdx === -1) return 'pending';

    if (thisIdx < currentIdx) return 'done';
    if (thisIdx === currentIdx) {
        if (isGenerating) return 'active';
        return 'error';
    }
    return 'pending';
}

const LabelMonitoringModal: React.FC<LabelMonitoringModalProps> = ({ status, onClose, algorithmName }) => {
    if (!status) return null;

    const currentStep = status.current_step;
    const isGenerating = status.is_generating;
    const isError = currentStep === 'ERROR' || !!status.error;
    const isCompleted = currentStep === 'COMPLETED';

    return (
        <div className="modal-overlay" style={{ 
            zIndex: 1000, 
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0,0,0,0.6)'
        }}>
            <div className="modal-content" style={{ 
                maxWidth: '520px', 
                padding: '40px',
                borderRadius: '16px',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
                <h2 style={{ 
                    marginTop: 0, 
                    marginBottom: '12px', 
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    letterSpacing: '-0.025em'
                }}>
                    {isCompleted ? '✅ Labels Generate' : isError ? '❌ Errore Generazione' : '⚙️ Generazione Label'}
                </h2>
                
                {algorithmName && (
                    <div style={{ 
                        fontSize: '0.85rem', 
                        color: 'var(--primary-color)', 
                        marginBottom: '32px',
                        padding: '6px 12px',
                        background: 'rgba(var(--primary-rgb, 99,102,241), 0.1)',
                        borderRadius: '20px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontWeight: 500
                    }}>
                        <i className="fas fa-code-branch" style={{ marginRight: '8px' }}></i>
                        Algoritmo: <strong style={{ marginLeft: '4px' }}>{algorithmName}</strong>
                    </div>
                )}

                {/* Step checklist */}
                <div style={{ marginBottom: '24px' }}>
                    {STEPS.map(step => {
                        const state = getStepState(step.key, currentStep, isGenerating, isError);

                        return (
                            <div key={step.key} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 0',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                opacity: state === 'pending' ? 0.4 : 1,
                                transition: 'opacity 0.3s ease',
                            }}>
                                {/* Icon */}
                                <div style={{ width: '24px', textAlign: 'center', flexShrink: 0 }}>
                                    {state === 'done' && <span style={{ color: '#22c55e', fontSize: '1.2rem', fontWeight: 'bold' }}>✓</span>}
                                    {state === 'active' && (
                                        <span style={{
                                            display: 'inline-block',
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid var(--primary-color)',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite',
                                        }} />
                                    )}
                                    {state === 'error' && <span style={{ color: '#ef4444', fontSize: '1.2rem', fontWeight: 'bold' }}>✗</span>}
                                    {state === 'pending' && <span style={{ color: '#ccc', fontSize: '1rem' }}>○</span>}
                                </div>

                                {/* Label */}
                                <span style={{
                                    flex: 1,
                                    fontWeight: state === 'active' ? 600 : 400,
                                    color: state === 'error' ? '#ef4444' : state === 'done' ? '#22c55e' : 'inherit',
                                }}>
                                    {step.label}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Status message or error */}
                {(status.last_status_message || status.error) && (
                    <div style={{
                        fontSize: '0.85rem',
                        color: isError ? '#ef4444' : '#666',
                        background: isError ? '#fef2f2' : '#f8f9fa',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        border: isError ? '1px solid #fee2e2' : '1px solid #e9ecef'
                    }}>
                        {status.error || status.last_status_message}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                        className={`btn ${isError ? 'btn-danger' : 'btn-primary'}`}
                        onClick={onClose}
                        disabled={isGenerating && !isError && !isCompleted}
                        style={{ minWidth: '120px' }}
                    >
                        {isGenerating && !isError && !isCompleted ? 'ATTENDERE...' : 'CHIUDI'}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default LabelMonitoringModal;
